import dayjs, { Dayjs } from 'dayjs'

export const DASHBOARD_TABS = [
  'overview',
  'inventory',
  'sales',
  'applications',
  'repair',
  'payroll',
] as const

export type DashboardTabId = (typeof DASHBOARD_TABS)[number]

export const DASHBOARD_RANGES = [
  'this_month',
  'last_month',
  'two_months_ago',
  'three_months_ago',
  'four_months_ago',
  'this_year',
  'custom',
] as const

export type DashboardRangeId = (typeof DASHBOARD_RANGES)[number]

const TAB_SET = new Set<string>(DASHBOARD_TABS)
const RANGE_SET = new Set<string>(DASHBOARD_RANGES)

export const isDashboardTab = (tab: string | undefined): tab is DashboardTabId =>
  Boolean(tab && TAB_SET.has(tab))

export const dashboardTabIndex = (tab: string | undefined): number => {
  if (!isDashboardTab(tab)) return 0
  return DASHBOARD_TABS.indexOf(tab)
}

export const dashboardTabId = (index: number): DashboardTabId =>
  DASHBOARD_TABS[index] ?? 'overview'

export const parseDashboardTab = (value: string | null): DashboardTabId =>
  value && isDashboardTab(value) ? value : 'overview'

/**
 * Old `/admin/sales` style paths. `inventory` is omitted on purpose:
 * `/admin/inventory` is the inventory app, not a dashboard tab.
 */
export const DASHBOARD_LEGACY_PATH_TABS = [
  'overview',
  'sales',
  'applications',
  'repair',
  'payroll',
] as const

export const isOwnerDashboardPath = (pathname: string): boolean => {
  const path = pathname.replace(/\/+$/, '') || '/'
  return path === '/admin' || path === '/dashboard'
}

export const parseDashboardRange = (value: string | null): DashboardRangeId =>
  value && RANGE_SET.has(value) ? (value as DashboardRangeId) : 'this_month'

export const parseDashboardDay = (value: string | null): Dayjs | null => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed : null
}

/** Presets omit `range` when it is the default. Custom dates use `from` and `to`. */
export const writeDashboardRange = (
  params: URLSearchParams,
  range: string,
  dates?: { from?: string | null; to?: string | null },
): void => {
  const nextRange = parseDashboardRange(range === 'this_month' ? null : range)
  if (nextRange === 'this_month') params.delete('range')
  else params.set('range', nextRange)

  if (nextRange !== 'custom') {
    params.delete('from')
    params.delete('to')
    return
  }

  if (!dates) return
  if (dates.from) params.set('from', dates.from)
  else if (dates.from === null) params.delete('from')
  if (dates.to) params.set('to', dates.to)
  else if (dates.to === null) params.delete('to')
}
